import { useEffect, useMemo, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { CheckCircle } from '@emotion-icons/material/CheckCircle'
import { CaretUp } from '@emotion-icons/fa-solid/CaretUp'
import { CaretDown } from '@emotion-icons/fa-solid/CaretDown'
import { Grid, IconButton, Link, Typography } from '@mui/material'
import { Colors, Shapes } from 'src/components/theme'
import { COLUMN_WIDTH } from 'src/views/subscriptions/table/header'
import { SubscriptionSection } from 'src/constants/subscriptions'
import { SubscriptionFunctionalityIcon } from 'src/views/subscriptions/icons/functionality'
import { SubscriptionReportsIcon } from 'src/views/subscriptions/icons/reports'
import { SubscriptionToolsIcon } from 'src/views/subscriptions/icons/tools'

const Wrapper = styled.div<{ color?: string }>`
  position: relative;
  padding-bottom: ${Shapes.spacing[2]}px;
  height: auto;
  :before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    // Prevent it from being continuous with other rows
    height: calc(100% - ${Shapes.spacing[2]}px);
    width: ${Shapes.spacing[1]}px;
    background-color: ${(props) => props.color || Colors.blueDark};
  }
  :last-of-type {
    :before {
      height: 100%;
    }
  }
`

const CollapseRow = styled(Grid)``
const CollapseColumn = styled(Grid)`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${Shapes.spacing[2]}px;
  padding: ${Shapes.spacing[4]}px;
  border-right: 1px solid ${Colors.border};
  :last-of-type {
    border-right: none;
    :before {
      display: none;
    }
  }
  // Border extension that makes all columns look seamless
  :before {
    content: '';
    position: absolute;
    bottom: -${Shapes.spacing[2]}px;
    right: -1px;
    height: ${Shapes.spacing[2]}px;
    width: 1px;
    background-color: ${Colors.border};
  }
`
const CollapsedColumnHeader = styled(Typography)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  span {
    display: flex;
    align-items: center;
    gap: ${Shapes.spacing[2]}px;
  }
`

const FeatureRow = styled(Grid)<{ index: number }>`
  background-color: ${(props) => (props.index % 2 === 0 ? Colors.bgWhite : Colors.neutral50)};
`
const FeatureNameColumn = styled(CollapseColumn)`
  flex-direction: row;
  padding-inline: ${Shapes.spacing[4]}px;
  align-items: center;
`
const FeatureName = styled(Link)`
  color: ${Colors.textMedium};
  text-decoration: none;
  :hover {
    color: ${Colors.textDark};
    text-decoration: dashed underline;
  }
`
const FeatureAccessColumn = styled(FeatureNameColumn)`
  justify-content: center;
`

type SubscriptionTableRowProps = {
  section: SubscriptionSection
}
export const SubscriptionTableRow = (props: SubscriptionTableRowProps) => {
  const { section } = props
  const [expanded, setExpanded] = useState(true)
  const ref = useRef<HTMLDivElement>(null)
  const initialLoad = useRef(false)

  const icon = useMemo(() => {
    switch (section.title.toLowerCase()) {
      case 'functionality':
        return <SubscriptionFunctionalityIcon color={section.color} size={24} />
      case 'reports':
        return <SubscriptionReportsIcon color={section.color} size={24} />
      case 'tools':
        return <SubscriptionToolsIcon color={section.color} size={24} />
      default:
        return null
    }
  }, [section])

  const onToggleExpand = () => {
    setExpanded(!expanded)
    if (initialLoad.current === false) {
      initialLoad.current = true
    }
  }

  useEffect(() => {
    if (expanded && ref.current && initialLoad.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [expanded])

  return (
    <Wrapper color={section.color}>
      <CollapseRow container>
        {/* Collapsed Row with toggle */}
        <CollapseColumn item xs={COLUMN_WIDTH.primary}>
          <CollapsedColumnHeader variant="textmd" color={Colors.textBlack}>
            <span>
              {icon}
              {section.title}
            </span>
            <IconButton onClick={onToggleExpand}>
              {expanded ? (
                <CaretUp size={16} color={Colors.neutral300} />
              ) : (
                <CaretDown size={16} color={Colors.neutral300} />
              )}
            </IconButton>
          </CollapsedColumnHeader>
          <Typography variant="text1m" color={Colors.textMedium}>
            {section.subtitle}
          </Typography>
        </CollapseColumn>
        <CollapseColumn item xs={COLUMN_WIDTH.other} />
        <CollapseColumn item xs={COLUMN_WIDTH.other} />
        <CollapseColumn item xs={COLUMN_WIDTH.other} />
        <CollapseColumn item xs={COLUMN_WIDTH.other} />
      </CollapseRow>

      {/* Expanded Feature Rows */}
      <div>
        {expanded &&
          section.rows.map((row, index) => (
            <FeatureRow key={`${section.title}-${row.title}`} ref={index === 0 ? ref : null} index={index} container>
              <FeatureNameColumn item xs={COLUMN_WIDTH.primary}>
                <FeatureName
                  href="https://www.alphamap.com/blog"
                  target="_blank"
                  variant="textmdm"
                  color={Colors.textMedium}>
                  {row.title}
                </FeatureName>
              </FeatureNameColumn>
              <FeatureAccessColumn item xs={COLUMN_WIDTH.other}>
                {row.access.free ? <CheckCircle size={14} color={Colors.blueDark} /> : null}
              </FeatureAccessColumn>
              <FeatureAccessColumn item xs={COLUMN_WIDTH.other}>
                {row.access.entry ? <CheckCircle size={14} color={Colors.blueDark} /> : null}
              </FeatureAccessColumn>
              <FeatureAccessColumn item xs={COLUMN_WIDTH.other}>
                {row.access.intermediate ? <CheckCircle size={14} color={Colors.blueDark} /> : null}
              </FeatureAccessColumn>
              <FeatureAccessColumn item xs={COLUMN_WIDTH.other}>
                {row.access.full ? <CheckCircle size={14} color={Colors.blueDark} /> : null}
              </FeatureAccessColumn>
            </FeatureRow>
          ))}
      </div>
    </Wrapper>
  )
}
